import type { Bounds, ImageEffect, ImageElement, SceneElement, VectorElement } from './types.js';

/**
 * The Image and Vector placeholder panels, as fields rather than as widgets.
 *
 * Customily's Image Behavior / Vector Mode blocks are five checkboxes and two
 * dropdowns, and every one of them is a view onto a field this schema already
 * has: "Fill Area" is `fit`, "90 Degree Rotation Lock" is `shopper.rotateStep`,
 * "Single Color Tracing" is `colorize` + `colorizeMode`. Writing those mappings
 * once, here, is what keeps the studio panel, the importer and the form
 * generator agreeing about what a checkbox means — the alternative is three
 * copies of "grayscale means an effect entry, not a colorize", and the third
 * copy is always the one that is wrong.
 */

// ── Image type ───────────────────────────────────────────────────────────────

/** Customily's "Image Type" dropdown, verbatim in order: the default is first. */
export type ImageType = 'fullColor' | 'grayscale' | 'singleColorTracing';

export const IMAGE_TYPES: ReadonlyArray<{ key: ImageType; label: string }> = [
  { key: 'fullColor', label: 'Full Color' },
  { key: 'grayscale', label: 'Grayscale' },
  { key: 'singleColorTracing', label: 'Single Color Tracing' },
];

/**
 * Which of the three modes a layer is in.
 *
 * Tracing is checked first: a layer that carries both a `darkness` tint and a
 * greyscale effect is being traced to one ink, and reading it as "Grayscale"
 * would show the designer a control that does not describe what prints.
 */
export function imageTypeOf(el: ImageElement): ImageType {
  if (el.colorize && el.colorizeMode === 'darkness') return 'singleColorTracing';
  if ((el.effects ?? []).some((e) => e.kind === 'grayscale')) return 'grayscale';
  return 'fullColor';
}

/**
 * Puts a layer into one of the three modes, clearing whatever the other two
 * left behind.
 *
 * `colorize` is only dropped when it was the tracing tint (`darkness`): a
 * cut-out PNG tinted through its own alpha is single-colour clipart, which is
 * a different feature and lives on happily in Full Color.
 */
export function applyImageType(el: ImageElement, type: ImageType): ImageElement {
  const withoutGray = (el.effects ?? []).filter((e) => e.kind !== 'grayscale');
  const next: ImageElement = { ...el };
  if (el.colorizeMode === 'darkness') {
    delete next.colorize;
    delete next.colorizeMode;
  }

  if (type === 'grayscale') {
    next.effects = [...withoutGray, { kind: 'grayscale' }];
    return next;
  }

  next.effects = withoutGray.length > 0 ? withoutGray : undefined;
  if (type === 'singleColorTracing') {
    next.colorize = el.colorize ?? { hex: '#000000', alpha: 1 };
    next.colorizeMode = 'darkness';
  }
  return next;
}

// ── Vector type ──────────────────────────────────────────────────────────────

/** Customily's "Vector type" dropdown: Full Color (default) / Single Color. */
export type VectorType = 'fullColor' | 'singleColor';

export const VECTOR_TYPES: ReadonlyArray<{ key: VectorType; label: string }> = [
  { key: 'fullColor', label: 'Full Color' },
  { key: 'singleColor', label: 'Single Color' },
];

export function vectorTypeOf(el: VectorElement): VectorType {
  return el.singleColor ? 'singleColor' : 'fullColor';
}

/**
 * Switches an artwork layer between its own colours and one flat ink.
 *
 * The named colour SLOTS are left alone either way: `singleColor` already wins
 * over them in the renderer, so turning single colour off must give the file
 * its per-ink palette back rather than an empty list the designer has to
 * rebuild.
 */
export function applyVectorType(el: VectorElement, type: VectorType): VectorElement {
  if (type === 'fullColor') {
    const next = { ...el };
    delete next.singleColor;
    return next;
  }
  return { ...el, singleColor: el.singleColor ?? el.colorChoices?.[0] ?? { hex: '#000000', alpha: 1 } };
}

// ── Movement & scaling ───────────────────────────────────────────────────────

/**
 * Customily's "Movement & Scaling" checkbox grid, in its own words.
 *
 * All five default to false — an untouched placeholder frames the shopper's
 * photo inside its box, rotates freely, and may be dragged and resized.
 */
export interface MovementFlags {
  /** "Fill Area": the picture covers the whole box, cropping the overflow. */
  fillArea: boolean;
  /** "90 Degree Rotation Lock": rotation snaps to quarter turns. */
  rotationLock90: boolean;
  /** "Limit image size to placeholder": a photo outside the box blocks the order. */
  limitToPlaceholder: boolean;
  /** "Lock movement". */
  lockMovement: boolean;
  /** "Lock scaling". */
  lockScaling: boolean;
}

type Placeholder = ImageElement | VectorElement;

export function movementFlags(el: Placeholder): MovementFlags {
  return {
    fillArea: el.fit === 'cover',
    rotationLock90: el.shopper?.rotateStep === 90,
    limitToPlaceholder: el.bounds?.mode === 'limit',
    lockMovement: el.shopper?.move === false,
    lockScaling: el.shopper?.scale === false,
  };
}

/**
 * Writes some or all of the five flags onto a layer.
 *
 * Only the flags actually named are touched, because the grid is five
 * independent checkboxes and clicking one must not quietly re-assert the other
 * four — a layer whose bounds are a drawn CLIP would otherwise lose the clip
 * the moment somebody ticked "Lock scaling".
 */
export function applyMovementFlags<T extends Placeholder>(
  el: T,
  flags: Partial<MovementFlags>,
): T {
  const next = { ...el } as T;
  const shopper = { ...(el.shopper ?? {}) };
  let touchedShopper = false;

  if (flags.fillArea !== undefined) {
    // Off is `contain`, not "unset": an unset fit means `cover` in the
    // renderer, which is the box the checkbox is meant to turn OFF.
    next.fit = flags.fillArea ? 'cover' : 'contain';
  }
  if (flags.rotationLock90 !== undefined) {
    if (flags.rotationLock90) shopper.rotateStep = 90;
    else delete shopper.rotateStep;
    touchedShopper = true;
  }
  if (flags.lockMovement !== undefined) {
    shopper.move = !flags.lockMovement;
    touchedShopper = true;
  }
  if (flags.lockScaling !== undefined) {
    shopper.scale = !flags.lockScaling;
    touchedShopper = true;
  }
  if (flags.limitToPlaceholder !== undefined) {
    if (flags.limitToPlaceholder) {
      // `limit` is the commit-time check, not a drawn crop. A layer already
      // carrying a drawn clip keeps it — a clip is the stronger promise.
      next.bounds =
        el.bounds?.mode === 'clip' ? el.bounds : ({ ...(el.bounds ?? {}), mode: 'limit' } as Bounds);
    } else if (el.bounds?.mode === 'limit') {
      delete next.bounds;
    }
  }
  if (touchedShopper) next.shopper = shopper;
  return next;
}

// ── Mask presets ─────────────────────────────────────────────────────────────

/**
 * The nineteen shapes in Customily's "Select or Upload a Shape" grid.
 *
 * Customily prints no captions under them — they are identified by their
 * silhouette alone — so the keys are ours, and they are what
 * `POST /v1/assets/builtin { name: 'mask-<key>' }` installs. The labels exist
 * for the picker's tooltips and for screen readers, which have nothing else to
 * read on a grid of black shapes.
 */
export const MASK_PRESETS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'circle-plain', label: 'Circle' },
  { key: 'circle-scallop-thin', label: 'Scalloped circle — fine' },
  { key: 'circle-scallop-bold', label: 'Scalloped circle — bold' },
  { key: 'circle-scallop-var', label: 'Scalloped circle — mixed' },
  { key: 'square-dashed', label: 'Dashed square' },
  { key: 'square-dashed-alt', label: 'Dashed square — wide' },
  { key: 'square-rounded', label: 'Rounded square' },
  { key: 'square-sharp', label: 'Square' },
  { key: 'arch', label: 'Arch' },
  { key: 'badge', label: 'Badge' },
  { key: 'heart-classic', label: 'Heart' },
  { key: 'heart-ribbon', label: 'Heart — ribbon' },
  { key: 'heart-round', label: 'Heart — rounded' },
  { key: 'star-5', label: 'Star — 5 points' },
  { key: 'star-8', label: 'Star — 8 points' },
  { key: 'sunburst', label: 'Sunburst' },
  { key: 'cloud', label: 'Cloud' },
  { key: 'flower', label: 'Flower' },
  { key: 'clover', label: 'Clover' },
];

/** The built-in asset name a preset installs under. */
export const maskAssetName = (key: string): string => `mask-${key}`;

// ── AI style presets ─────────────────────────────────────────────────────────

export type ImageEffectCategory = 'tools' | 'styleModifiers' | 'customPrompts';

export interface AiStylePreset {
  key: string;
  /** Customily's own tile caption, verbatim. */
  label: string;
  /** The prompt the effect carries. Baked in, so picking a tile needs no call. */
  prompt: string;
  category: ImageEffectCategory;
}

/**
 * Customily's own one-line description of a style tile, verbatim:
 * "Transform your shopper's images into a <Name>-style masterpiece."
 *
 * It IS the prompt. Customily hangs the same sentence off every style modifier
 * and hands it to whichever provider the merchant connected, which is why the
 * tiles behave like a vocabulary rather than like twenty tuned pipelines.
 * Copying the sentence rather than inventing a better one is deliberate: a
 * merchant moving a product across must get the picture they were getting.
 */
export const aiStylePrompt = (label: string): string =>
  `Transform your shopper's images into a ${label}-style masterpiece.`;

const STYLE_TILES = [
  'Avatar styles',
  'Oil Painting',
  'Renaissance Painting',
  'Whimsical Anime',
  'Playful Cartoon',
  'Anime',
  'Cartoon',
  'Cyberpunk',
  'Retro Cartoon',
  'B&W Comic',
  'Kawaii Cartoon',
  'Yellowtoon',
  'Pixel Art',
  'Cute Retro Cartoon',
  '3D Art',
] as const;

/** `Avatar styles` -> `avatar-styles`; `B&W Comic` -> `b-w-comic`. */
const presetKey = (label: string): string =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Every style a tile can apply, with the prompt baked in.
 *
 * Two entries are not styles but shapes for one: "Custom Prompt" starts empty
 * so the merchant writes their own, and "Text to Image" paints from words with
 * no upload at all — Customily lists both under "Custom Prompts" and so do we.
 */
export const AI_STYLE_PRESETS: ReadonlyArray<AiStylePreset> = [
  ...STYLE_TILES.map((label) => ({
    key: presetKey(label),
    label,
    prompt: aiStylePrompt(label),
    category: 'styleModifiers' as const,
  })),
  {
    key: 'custom-prompt',
    label: 'Custom Prompt',
    prompt: '',
    category: 'customPrompts',
  },
  {
    key: 'text-to-image',
    label: 'Text to Image',
    prompt: '',
    category: 'customPrompts',
  },
];

export const aiStylePreset = (key: string): AiStylePreset | undefined =>
  AI_STYLE_PRESETS.find((p) => p.key === key);

// ── The effects catalogue ────────────────────────────────────────────────────

export interface ImageEffectTile {
  /** Customily's tile caption, verbatim — what the modal shows and searches. */
  label: string;
  category: ImageEffectCategory;
  /** The effect kind this tile adds, or `aiStyle` when it drives a preset. */
  effect: ImageEffect['kind'];
  /** For an `aiStyle` tile, which `AI_STYLE_PRESETS` entry it carries. */
  presetKey?: string;
  /**
   * The shop must have an image-model key configured before this tile does
   * anything — the same OpenAI gate `/v1/assets/generate` applies.
   *
   * Only the AI styles are gated. Background removal, the face cut and the
   * blur have local fallbacks in this stack (a shop with no cutout.pro key
   * still gets a cut-out), and every colour effect is arithmetic we do here.
   */
  requiresKey: boolean;
  /** One line for the hover card. */
  description: string;
}

/**
 * The "Image Effects" modal, section by section, in Customily's own order.
 *
 * Two of its tools duplicate one of ours on purpose: Customily sells "Remove
 * Background" and "Remove Solid Background" as separate providers, and the
 * second is what our `colorToAlpha` does when the backdrop is flat — but a
 * merchant looking for the tile they know must find it under the name they
 * know, so both are listed and both resolve to something real.
 */
export const IMAGE_EFFECT_CATALOGUE: ReadonlyArray<ImageEffectTile> = [
  {
    label: 'Remove Background',
    category: 'tools',
    effect: 'removeBackground',
    requiresKey: false,
    description: 'Cuts the subject out of its background.',
  },
  {
    label: 'Blur Background',
    category: 'tools',
    effect: 'blurBackground',
    requiresKey: false,
    description: 'Keeps the subject sharp and softens everything behind it.',
  },
  {
    label: 'Face Cut Out',
    category: 'tools',
    effect: 'faceCutout',
    requiresKey: false,
    description: 'Keeps the head — face and hair — and drops the rest.',
  },
  {
    label: 'Enhance Photo',
    category: 'tools',
    effect: 'enhance',
    requiresKey: false,
    description: 'Upscales and sharpens the picture before it prints.',
  },
  {
    label: 'Remove Solid Background',
    category: 'tools',
    effect: 'colorToAlpha',
    requiresKey: false,
    description: 'Drops one flat backdrop colour to transparent.',
  },
  {
    label: 'Color to Alpha',
    category: 'tools',
    effect: 'colorToAlpha',
    requiresKey: false,
    description: 'Picks a colour and makes every pixel near it transparent.',
  },
  {
    label: 'Watercolor Effect',
    category: 'styleModifiers',
    effect: 'watercolor',
    requiresKey: false,
    description: 'A watercolour wash: softened detail, lifted colour, soft edges.',
  },
  {
    label: 'Pencil Drawing',
    category: 'styleModifiers',
    effect: 'pencil',
    requiresKey: false,
    description: 'A pencil sketch: grey tones, darkened edges, paper left white.',
  },
  {
    label: 'Edge',
    category: 'styleModifiers',
    effect: 'edge',
    requiresKey: false,
    description: 'Reduces the picture to its edges.',
  },
  {
    label: 'Invert',
    category: 'styleModifiers',
    effect: 'invert',
    requiresKey: false,
    description: 'Inverts every colour.',
  },
  {
    label: 'Sepia',
    category: 'styleModifiers',
    effect: 'sepia',
    requiresKey: false,
    description: 'An aged, warm-brown tone.',
  },
  ...AI_STYLE_PRESETS.map(
    (preset): ImageEffectTile => ({
      label: preset.label,
      category: preset.category,
      effect: 'aiStyle',
      presetKey: preset.key,
      requiresKey: true,
      description:
        preset.category === 'styleModifiers'
          ? preset.prompt
          : preset.key === 'text-to-image'
            ? 'Paints the picture from words alone — no upload needed.'
            : 'Write your own prompt for the exact effect you want.',
    }),
  ),
];

export const imageEffectTile = (label: string): ImageEffectTile | undefined =>
  IMAGE_EFFECT_CATALOGUE.find((t) => t.label === label);

/**
 * The tile a name from somewhere else means — Customily's own filter names, an
 * older spelling, a key rather than a caption.
 *
 * Written for the importer, which meets these as raw strings on a layer
 * (`CustomilyImageFilter: "PencilDrawing"`, `imageEffects: ["Sepia"]`) and has
 * nothing but the string to go on. Matching is by slug, so
 * "Pencil Drawing" / "pencil-drawing" / "PencilDrawing" / "pencil_drawing" all
 * find the same tile, and an `aiStyle` tile is also reachable by its preset key.
 *
 * Deliberately NOT fuzzy beyond that: guessing that "Vintage" means Sepia would
 * put a look on a live product that nobody chose.
 */
export function matchImageEffect(name: string): ImageEffectTile | undefined {
  const wanted = presetKey(
    // "PencilDrawing" and "removeBackgroundCOP" arrive camel-cased; a boundary
    // between a lower-case letter and an upper-case one is a word break.
    String(name ?? '').replace(/([a-z0-9])([A-Z])/g, '$1-$2'),
  );
  if (!wanted) return undefined;
  return (
    IMAGE_EFFECT_CATALOGUE.find((t) => presetKey(t.label) === wanted) ??
    IMAGE_EFFECT_CATALOGUE.find((t) => t.presetKey === wanted)
  );
}

/**
 * The effect a tile adds to a layer, ready to push onto `effects`.
 *
 * Returns undefined for a tile that needs an argument nobody supplied — a
 * "Custom Prompt" with no prompt is a call to the image model that costs money
 * and paints nothing.
 */
export function effectForTile(
  tile: ImageEffectTile,
  options: { prompt?: string; color?: ImageElement['colorize']; tolerance?: number } = {},
): ImageEffect | undefined {
  switch (tile.effect) {
    case 'aiStyle': {
      const preset = tile.presetKey ? aiStylePreset(tile.presetKey) : undefined;
      const prompt = (options.prompt ?? preset?.prompt ?? '').trim();
      return prompt ? { kind: 'aiStyle', prompt } : undefined;
    }
    case 'colorToAlpha':
      return {
        kind: 'colorToAlpha',
        // White is the backdrop nine artwork files in ten arrive on.
        color: options.color ?? { hex: '#FFFFFF', alpha: 1 },
        ...(options.tolerance === undefined ? {} : { tolerance: options.tolerance }),
      };
    case 'blurBackground':
    case 'removeBackground':
    case 'faceCutout':
    case 'enhance':
    case 'grayscale':
    case 'sepia':
    case 'invert':
    case 'pencil':
    case 'watercolor':
    case 'edge':
      return { kind: tile.effect } as ImageEffect;
    default:
      // brightness/contrast/threshold/outline have no tile: they are sliders on
      // their own panels, not one-click looks.
      return undefined;
  }
}

// ── Library-backed slots ─────────────────────────────────────────────────────

/** True for the Dynamic Image / Dynamic Vector shape: a whole collection. */
export function isLibrarySlot(el: SceneElement): el is ImageElement | VectorElement {
  return (
    (el.type === 'image' || el.type === 'vector') && el.source.kind === 'library'
  );
}

/** The collection a Dynamic Image / Dynamic Vector layer draws from. */
export function librarySlotId(el: SceneElement): string | undefined {
  return isLibrarySlot(el) && el.source.kind === 'library' ? el.source.libraryId : undefined;
}
